"use client";
import type { External } from "@gettactic/api";
import {
  actionDelete,
  DataMan,
  type DataManAction,
  type DataManColumDef
} from "@gettactic/components/src/data-display/data-man";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { SearchInput } from "@gettactic/components/src/controls/SearchInput/SearchInput";
import { groupSharedKey } from "@/features/integrations/components/external-ids/IntegrationsExternalIdsList";
import { api } from "@/lib/api/api";

export interface IntegrationsExternalIdsTableProps {
  data: External[];
  invalidateQuery: () => Promise<void>;
}

type AvailableIntegrations = "ioffice";
const integrationByType: Record<AvailableIntegrations, string> = {
  ioffice: "iOffice"
};

type ExternalWithBulkKey = External & { _bulkKey: string };

export function IntegrationsExternalIdsTable({
  data: rawData,
  invalidateQuery
}: IntegrationsExternalIdsTableProps) {
  const [query, setQuery] = useState("");

  const filteredData = useMemo(() => {
    const q = query.toLowerCase().trim();
    const dataWithKeys = rawData.map((x) => ({
      ...x,
      _bulkKey: groupSharedKey(x)
    }));
    return q.length
      ? dataWithKeys.filter((x) => x.name?.toLowerCase().includes(q))
      : dataWithKeys;
  }, [query, rawData]);

  const columns: DataManColumDef<ExternalWithBulkKey>[] = useMemo(
    () => [
      {
        key: "external_id",
        title: "Id",
        render: (data) => (
          <div title={data.external_id ?? undefined}>{data.external_id}</div>
        )
      },
      {
        key: "name",
        title: "Type",
        render: (data) => (
          <div title={data.name ?? undefined}>
            {integrationByType[data.name as AvailableIntegrations]}
          </div>
        )
      }
    ],
    []
  );

  const actions: DataManAction[] = useMemo(
    () => [
      actionDelete(
        async ({ target, targetRecords }) => {
          const records = targetRecords.length > 0 ? targetRecords : [target];

          const results = await Promise.all(
            records.map((external: External) =>
              api.client.externals.deleteExternalId(external.id)
            )
          );

          const hasError = results.some((res) => res.error);
          if (hasError) {
            toast.error("Sorry we couldn't delete this external IDs");
          } else {
            toast.success(
              records.length === 1
                ? "Deleted successfully"
                : "Deleted successfully"
            );
          }

          await invalidateQuery();
        },
        {
          allowBulk: true,
          confirm: {
            title: "Delete External ID",
            message:
              "Are you sure you want to delete? You cannot restore this item once it has been deleted."
          }
        }
      )
    ],
    [invalidateQuery]
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
      <div className="flex items-center justify-between gap-4 px-1 sm:gap-16">
        <SearchInput query={query} setQuery={setQuery} />
      </div>
      <div className="min-h-0 flex-1 px-1 relative">
        <DataMan
          data={filteredData}
          columns={columns}
          actions={actions}
          dataIdKey="_bulkKey"
          allowBulkSelection
        />
      </div>
    </div>
  );
}
